import { ref, computed, type Ref } from "vue";

// Minimal shape a compare-selection set needs: something to key items by. Feature-specific compare
// items (e.g. Library's `CompareItem`) extend this with whatever extra fields their tray/chip needs.
export interface CompareSelectable {
  id: string;
}

// Generic "capped compare-selection set": toggle items in/out, optionally reject adds past a max count,
// and derive ids/count from the current set. Extracted from useLibrarySelection (RTLCA-5094) so other
// compare-style selections (e.g. project compare) can reuse the same toggle/removal semantics without
// duplicating them.
//
// Call this once per feature module at module scope to get an independent module-scoped singleton —
// see useLibrarySelection.ts for the pattern.
export function createCompareSelection<CompareItemType extends CompareSelectable>() {
  // Cast away Vue's deep ref-unwrapping of the generic element type — with a bare `ref<T[]>` over a generic
  // T, `.value` widens to `(T | UnwrapRefSimple<T>)[]` and reassignment (spread/filter) no longer type-checks.
  const selectedItems = ref<CompareItemType[]>([]) as Ref<CompareItemType[]>;

  // ids are derived, so consumers that only need the id list (e.g. a `?ids=` builder) stay in sync
  // without holding their own copy.
  const selectedIds = computed(() => selectedItems.value.map((item) => item.id));
  const selectedCount = computed(() => selectedItems.value.length);

  // Add/remove an item from the compare set. Returns false when an *add* was rejected because the cap was
  // already reached; true otherwise (removals always succeed) — callers use it to surface a "limit reached" hint.
  function toggleItem(item: CompareItemType, maxCount = Infinity) {
    const index = selectedItems.value.findIndex((selected) => selected.id === item.id);
    if (index > -1) {
      selectedItems.value = selectedItems.value.filter((_, currentIndex) => currentIndex !== index);
      return true;
    }
    if (selectedItems.value.length >= maxCount) return false;
    selectedItems.value = [...selectedItems.value, item];
    return true;
  }

  // Remove a single item by id (e.g. the tray's per-chip x).
  function removeItem(id: string) {
    selectedItems.value = selectedItems.value.filter((item) => item.id !== id);
  }

  function clearSelection() {
    selectedItems.value = [];
  }

  // Named alias for the "clear everything" call sites (tab switch, compare-page Clear).
  function deactivate() {
    selectedItems.value = [];
  }

  function isSelected(id: string) {
    return selectedItems.value.some((item) => item.id === id);
  }

  return {
    // state
    selectedItems,
    selectedIds,
    selectedCount,

    // actions
    toggleItem,
    removeItem,
    clearSelection,
    deactivate,
    isSelected,
  };
}
